import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, useChat } from "randmarcomps";
import { visuallyHidden } from "@mui/utils";
import { inject, observer } from "mobx-react";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { FormattedNumber } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Requisition } from "../../stores/document/Document";
import { RootStore } from "../../stores/rootStore";
import CancelRequisition from '../document/actions/CancelRequisition';
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import { Sparkles } from "lucide-react";

interface IProps {
  store?: RootStore;
  requisitions?: Requisition[];
}

const surfaceTokens = {
  cardBorder: "var(--color-border)",
  cardBg: "var(--color-card)",
  mutedBg: "var(--color-muted)",
  mutedFg: "var(--color-muted-foreground)",
  accent: "var(--color-randmar-purple)",
};

const ManufacturerRequisitionsGrid: React.FC<IProps> = observer(({ store, requisitions }) => {
  const { applicationId } = useManufacturer();
  const [checkedRequisitions, setCheckedRequisitions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firstCheckedRequisition, setFirstCheckedRequisition] = useState<Requisition | undefined>(undefined);
  const [redirectPONumber, setRedirectPONumber] = useState("");
  const [sendToManufacturer, setSendToManufacturer] = useState(true);

  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { sendMessageWithClear } = useChat();

  const tableCaptionId = "draft-purchases-table-caption";
  const sendToggleId = "send-to-manufacturer-toggle";
  const totalStatusId = "draft-purchases-total-status";
  const dialogTitleId = "confirm-po-dialog-title";

  useEffect(() => {
    if (redirectPONumber) {
      navigate(Path.document(applicationId, redirectPONumber));
    }
  }, [redirectPONumber, applicationId, navigate]);

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCreatePoButtonClicked = useCallback(() => {
    setOpen(true);
  }, []);

  const handleSendManufacturerSwitch = useCallback(() => {
    setSendToManufacturer(prev => !prev);
  }, []);

  const handleChecked = useCallback((requisitionNumber: string, isChecked: boolean) => {
    setCheckedRequisitions(prev => {
      const newChecked = isChecked
        ? [...prev, requisitionNumber]
        : prev.filter(r => r !== requisitionNumber);

      if (prev.length === 0 && newChecked.length > 0) {
        setFirstCheckedRequisition(requisitions?.find(v => v.requisitionNumber === requisitionNumber));
      } else if (newChecked.length === 0) {
        setFirstCheckedRequisition(undefined);
      }

      return newChecked;
    });
  }, [requisitions]);

  const handleConfirmPoCreation = useCallback(async () => {
    const { documentsStore, snackStore } = store!;

    if (checkedRequisitions.length === 0) return;

    setLoading(true);

    const purchaseOrderNumber = await documentsStore.createPurchaseOrder(applicationId, checkedRequisitions, sendToManufacturer);

    if (purchaseOrderNumber.startsWith("PO") && purchaseOrderNumber.length === 9) {
      snackStore.showSuccess(`Purchase order successfully created.`);

      documentsStore.getRequisitions(applicationId);
      documentsStore.getPurchasesOrders(applicationId);

      setRedirectPONumber(purchaseOrderNumber);
    }
    else snackStore.showError(`Could not create the purchase order.`);

    setLoading(false);
    setOpen(false);
  }, [applicationId, store, checkedRequisitions, sendToManufacturer]);

  const handleAssistantPrompt = useCallback(() => {
    sendMessageWithClear("Please guide me to create a draft purchase order");
  }, [sendMessageWithClear]);

  const renderNoRequisitions = () => {
    const emptyStateDescriptionId = "draft-empty-description";
    return (
      <Card
        component="section"
        aria-labelledby={emptyStateDescriptionId}
        sx={{
          width: "100%",
          borderRadius: 5,
          border: "1px dashed rgba(88, 44, 131, 0.35)",
          background: "linear-gradient(180deg, rgba(244, 240, 255, 0.95), rgba(233, 224, 255, 0.9))",
          boxShadow: "0 25px 65px rgba(88, 44, 131, 0.18)",
          px: { xs: 3, md: 6 },
          py: { xs: 5, md: 8 },
        }}
      >
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Chip
            label="No draft POs yet"
            icon={<Sparkles className="h-4 w-4 text-[var(--color-randmar-purple)]" aria-hidden="true" />}
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              borderRadius: "999px",
              backgroundColor: "rgba(88, 44, 131, 0.09)",
              color: "var(--color-randmar-purple)",
            }}
          />
          <Stack spacing={1}>
            <Typography variant="h1" component="h2" sx={{ fontSize: { xs: "1.5rem", md: "2.25rem" } }}>
              Manually create a draft
            </Typography>
            <Typography
              id={emptyStateDescriptionId}
              variant="body1"
              sx={{ color: "rgba(15, 23, 42, 0.8)", maxWidth: 640, mx: "auto" }}
            >
              Launch with confidence by adding high moving items to a draft PO. As soon as you add items, they become
              available here.
            </Typography>
          </Stack>
          <Button
            type="button"
            onClick={handleAssistantPrompt}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[var(--color-randmar-purple)] shadow-lg shadow-[var(--color-randmar-purple)]/30 transition hover:-translate-y-0.5"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Create a draft with Randmar Assistant
          </Button>
          <Typography variant="caption" sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
            You can fine-tune automation later.
          </Typography>
        </Stack>
      </Card>
    );
  };

  const { manufacturerStore, warehousesStore, userStore } = store!;
  const { manufacturer } = manufacturerStore;

  if (!requisitions || requisitions.length === 0) return renderNoRequisitions();

  const total = checkedRequisitions.reduce((acc, v) => acc + (requisitions?.find(r => r.requisitionNumber === v)?.subTotal || 0), 0);
  const isMinimumOrder = total > (manufacturer?.defaultMinimumOrder || 0);
  const isSuperAdmin = userStore.isSuperAdmin();
  const selectionLocation = firstCheckedRequisition?.location;

  const userRequestedRequisition = useMemo(() => requisitions.slice()
    .map<Requisition>(v => {
      const normalizedRequestor =
        v.requestedBy === "Echo"
          ? "Classic Algorithm"
          : v.reference === "AI Generated"
            ? "AI Algorithm"
            : v.requestedBy;

      return {
        ...v,
        requestedBy: normalizedRequestor,
      };
    }).sort((a, b) => {
      const p2 = a.location!.localeCompare(b.location!);
      if (p2 !== 0) return p2;
      const p1 = b.requisitionDate! - a.requisitionDate!;
      if (p1 !== 0) return p1;
      const p3 = a.requestedBy!.localeCompare(b.requestedBy!);
      if (p3 !== 0) return p3;
      return a.requisitionNumber!.localeCompare(b.requisitionNumber!);
    }), [requisitions]);

  const renderDesktopTable = () => (
    <Card
      sx={{
        width: "100%",
        borderRadius: 4,
        border: `1px solid ${surfaceTokens.cardBorder}`,
        boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ borderRadius: 4, width: "100%", overflow: "auto" }}>
        <Table size="medium" aria-describedby={tableCaptionId} stickyHeader>
          <Box component="caption" id={tableCaptionId} sx={{ ...visuallyHidden }}>
            Select draft purchases that share a location to convert them into a purchase order.
          </Box>
          <TableHead>
            <TableRow sx={{ bgcolor: surfaceTokens.mutedBg }}>
              <TableCell component="th" scope="col" padding="checkbox" />
              <TableCell component="th" scope="col" align="left">Draft Purchase Number</TableCell>
              <TableCell component="th" scope="col" align="left">Location</TableCell>
              <TableCell component="th" scope="col" align="left">Draft Purchase Date</TableCell>
              <TableCell component="th" scope="col" align="left">Sub Total</TableCell>
              <TableCell component="th" scope="col" align="left">Requestor</TableCell>
              <TableCell component="th" scope="col" align="center" aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {userRequestedRequisition.length > 0 &&
              userRequestedRequisition.map((row, index) => {
                const requisitionNumber = row.requisitionNumber || "";
                const isChecked = checkedRequisitions.includes(requisitionNumber);
                const isLocked = Boolean(selectionLocation && row.location !== selectionLocation);
                const locationName = warehousesStore.getWarehouseNameFromCode(row.location);
                const requestor = row.requestedBy || "Unknown";

                return (
                  <TableRow
                    key={`Requisition-${requisitionNumber}-${index}`}
                    hover
                    tabIndex={-1}
                    sx={{
                      backgroundColor: isChecked ? "var(--color-accent)" : undefined,
                      transition: "background-color 150ms ease, transform 150ms ease",
                      '&:hover': {
                        backgroundColor: "rgba(88, 44, 131, 0.08)",
                      },
                      '&:focus-within': {
                        outline: `2px solid var(--color-ring)`,
                        outlineOffset: -2,
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        sx={{ m: 0 }}
                        disabled={isLocked}
                        checked={isChecked}
                        color="primary"
                        inputProps={{
                          'aria-label': `Select draft purchase ${requisitionNumber}`,
                        }}
                        onChange={e => handleChecked(requisitionNumber, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Link to={Path.document(applicationId, requisitionNumber)} className="font-semibold text-[var(--color-randmar-purple)] underline-offset-4 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-ring)]">
                        {requisitionNumber}
                      </Link>
                    </TableCell>
                    <TableCell align="left">
                      <Tooltip title={selectionLocation && row.location === selectionLocation ? "Matches selected location" : ""}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            paddingInline: 1.5,
                            paddingBlock: 0.5,
                            borderRadius: 999,
                            border: selectionLocation && row.location === selectionLocation ? `1px dashed ${surfaceTokens.accent}` : `1px solid ${surfaceTokens.cardBorder}`,
                            backgroundColor: "var(--color-card)",
                          }}
                        >
                          {locationName}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left">{FormatDateFromInt(row.requisitionDate)}</TableCell>
                    <TableCell align="left"><FormattedNumber value={row.subTotal || 0}  {...CurrencyUtils.format} /></TableCell>
                    <TableCell align="left">
                      <Chip
                        label={requestor}
                        size="small"
                        sx={{ backgroundColor: surfaceTokens.mutedBg, color: surfaceTokens.mutedFg }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <CancelRequisition requisitionNumber={requisitionNumber} fab withReload />
                    </TableCell>
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </Box>
    </Card>
  );

  const renderResponsiveCards = () => (
    <Stack spacing={2}>
      {userRequestedRequisition.map(row => {
        const requisitionNumber = row.requisitionNumber || "";
        const isChecked = checkedRequisitions.includes(requisitionNumber);
        const isLocked = Boolean(selectionLocation && row.location !== selectionLocation);
        const locationName = warehousesStore.getWarehouseNameFromCode(row.location);
        const requestor = row.requestedBy || "Unknown";

        return (
          <Card
            key={`responsive-${requisitionNumber}`}
            component="article"
            sx={{
              borderRadius: 4,
              border: `1px solid ${surfaceTokens.cardBorder}`,
              backgroundColor: surfaceTokens.cardBg,
              boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
            }}
          >
            <CardHeader
              title={
                <Link
                  to={Path.document(applicationId, requisitionNumber)}
                  className="font-semibold text-[var(--color-randmar-purple)] underline-offset-4 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-ring)]"
                >
                  {requisitionNumber}
                </Link>
              }
              subheader={`Drafted on ${FormatDateFromInt(row.requisitionDate)}`}
              action={
                <Checkbox
                  checked={isChecked}
                  disabled={isLocked}
                  color="primary"
                  onChange={e => handleChecked(requisitionNumber, e.target.checked)}
                  inputProps={{ 'aria-label': `Select draft purchase ${requisitionNumber}` }}
                />
              }
            />
            <CardContent sx={{ display: "grid", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="subtitle1">{locationName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Sub Total</Typography>
                <Typography variant="subtitle1">
                  <FormattedNumber value={row.subTotal || 0}  {...CurrencyUtils.format} />
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Requestor</Typography>
                <Chip label={requestor} size="small" sx={{ width: "fit-content", mt: 0.5 }} />
              </Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Warehouse must match to bundle drafts.
                </Typography>
                <CancelRequisition requisitionNumber={requisitionNumber} fab withReload />
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  return (
    <>
      {isSmallScreen ? renderResponsiveCards() : renderDesktopTable()}

      <Stack
        component="section"
        aria-live="polite"
        id={totalStatusId}
        marginTop={{ xs: 2, md: 0 }}
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        padding={3}
        borderRadius={4}
        sx={{
          border: `1px solid ${surfaceTokens.cardBorder}`,
          backgroundColor: surfaceTokens.cardBg,
          position: { md: "fixed" },
          bottom: { md: 24 },
          right: { md: 24 },
          minWidth: { md: 360 },
          boxShadow: "0 20px 45px rgba(15, 23, 42, 0.12)",
          zIndex: 15,
        }}
      >
        <Box>
          <Typography variant="h1">
            Total: <FormattedNumber value={total}  {...CurrencyUtils.format} />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {!isMinimumOrder ? (
              <>Missing <FormattedNumber value={(manufacturer?.defaultMinimumOrder || 0) - total}  {...CurrencyUtils.format} /></>
            ) : "Minimum met."}
          </Typography>
        </Box>
        <Button
          size="lg"
          id="ButtonCreatePo"
          variant={(!isMinimumOrder && isSuperAdmin) ? "destructive" : undefined}
          title="Create PO"
          aria-describedby={totalStatusId}
          disabled={(!isMinimumOrder && !isSuperAdmin) || checkedRequisitions.length === 0}
          onClick={handleCreatePoButtonClicked}
          className="w-full md:w-auto"
        >
          Generate a Purchase
        </Button>
      </Stack>

      <Dialog
        open={open}
        aria-labelledby={dialogTitleId}
        onOpenChange={(isOpen) => {
          if (!isOpen && !loading) {
            handleCancel();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle id={dialogTitleId} className="text-center w-full">
              Confirm purchase order creation
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="text-center p-4">
              <p className="mb-4">Preparing your purchase order…</p>
              <LinearProgress aria-label="Generating purchase order" />
            </div>
          ) : (
            <>
              <div className="py-4 space-y-4">
                <Typography variant="body1">
                  Total of the purchase order is <FormattedNumber value={total} {...CurrencyUtils.format} />
                </Typography>
                <div className="flex flex-col gap-2">
                  <FormControlLabel
                    control={
                      <Switch
                        id={sendToggleId}
                        color="primary"
                        checked={sendToManufacturer}
                        onChange={handleSendManufacturerSwitch}
                      />
                    }
                    label="Send purchase order to manufacturer"
                  />
                  <Typography variant="caption" color="text.secondary">
                    We will notify the manufacturer and share the PO immediately after it is generated.
                  </Typography>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCancel} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleConfirmPoCreation} disabled={loading}>
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});

export default inject("store")(ManufacturerRequisitionsGrid);
